"use client";

import { useEffect, useRef } from "react";

const NEAR_BOTTOM_THRESHOLD = 50;

export default function ChatCanvas({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track whether the user is near the bottom BEFORE a child mutation; if yes
  // we auto-scroll to bottom after the mutation lands. If the user has scrolled
  // up to re-read earlier content we stay put.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let nearBottom = true;

    const checkNearBottom = () => {
      nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_THRESHOLD;
    };

    el.addEventListener("scroll", checkNearBottom, { passive: true });
    checkNearBottom();

    const observer = new MutationObserver(() => {
      if (!nearBottom) return;
      // After the DOM update, scroll to bottom smoothly.
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      });
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });

    return () => {
      el.removeEventListener("scroll", checkNearBottom);
      observer.disconnect();
    };
  }, []);

  return (
    <div
      className="app-chat-canvas"
      style={{
        background: "#201F1F",
        flex: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* Atmosphere glow — kept inline on the chat page because the
          layout-level <AmbientGlow /> ends up behind chat-canvas's
          opaque background. Other pages render through the layout
          version. */}
      <div
        className="app-chat-glow"
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "clip",
          pointerEvents: "none",
        }}
        aria-hidden
      >
        <div
          style={{
            width: 600,
            height: 600,
            borderRadius: 9999,
            background: "rgba(134,53,246,0.1)",
            filter: "blur(60px)",
          }}
        />
      </div>

      {/* Scrollable content — spans the full canvas width so the scrollbar
          sits on the viewport's right edge. The message column inside is
          centered + capped at 720px. */}
      <div
        ref={scrollRef}
        className="app-scrollbar app-chat-scroll"
        style={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          flex: 1,
          overflowY: "auto",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 720,
            margin: "0 auto",
            padding: "60px 0",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
